type ValueOf<T> = T[keyof T]

/** 灯光作用目标，统一描述键盘、侧灯、音乐律动和底座不同灯区。 */
export const LIGHTING_TARGET = {
  /** 键盘主按键背光灯区。 */
  Keyboard: 'keyboard',
  /** 键盘侧边氛围灯区。 */
  Side: 'side',
  /** 键盘rgb指示灯区。 */
  Indicator: 'indicator',
  /** 键盘音乐律动（软件灯效，由系统/麦克风音频驱动逐键 RGB）。 */
  AudioRhythm: 'audio-rhythm',
  /** 底座外圈或底部灯带灯区。 */
  BaseLightBand: 'base-light-band',
  /** 底座铭牌、Logo 或屏幕周边灯区。 */
  BaseLightScreen: 'base-light-screen',
} as const

/** 灯效展示与协议领域分类。 */
export const LIGHTING_EFFECT_KIND = {
  /** 动态灯效，通常支持速度或自动变化。 */
  Dynamic: 'dynamic',
  /** 静态灯效，通常展示固定颜色或固定灯位图案。 */
  Static: 'static',
  /** 渐变灯效，通常需要配置多个端点或方向色值。 */
  Gradient: 'gradient',
  /** 指示类灯效，例如电量、充电状态等状态灯。 */
  Indicator: 'indicator',
  /** 氛围灯效 */
  Ambient: 'ambient',
} as const

/** 灯效允许用户编辑的字段。 */
export const LIGHTING_EDITABLE_FIELD = {
  /** 调整灯效整体亮度。 */
  Brightness: 'brightness',
  /** 调整动态灯效速度或呼吸频率。 */
  Speed: 'speed',
  /** 手动选择一个具体 RGB 单色。 */
  Color: 'color',
  /** 切换固件自动彩色循环/炫彩模式，不代表具体颜色值。 */
  ColorCycle: 'colorCycle',
  /** 编辑渐变灯效的多个颜色端点或渐变方向。 */
  Gradient: 'gradient',
  /**
   * 调整动态灯效的运动方向（正向/反向，如波浪/扩散/回旋类灯效）。
   * 与 Gradient 不同：Gradient 是静态双色渐变的色块方向（上下/左右），
   * Direction 是动态灯效的前进/扩散/旋转方向，对应领域扩展字段 direction。
   */
  Direction: 'direction',
} as const

export const KEYBOARD_LIGHTING_TARGETS = [
  LIGHTING_TARGET.Keyboard,
  LIGHTING_TARGET.Side,
  LIGHTING_TARGET.Indicator,
  LIGHTING_TARGET.AudioRhythm,
] as const

export const BASE_LIGHTING_TARGETS = [
  LIGHTING_TARGET.BaseLightBand,
  LIGHTING_TARGET.BaseLightScreen,
] as const

export type LightingTarget = ValueOf<typeof LIGHTING_TARGET>

export type KeyboardLightingTarget = (typeof KEYBOARD_LIGHTING_TARGETS)[number]

export type BaseLightingTarget = (typeof BASE_LIGHTING_TARGETS)[number]

export type LightingEffectKind = ValueOf<typeof LIGHTING_EFFECT_KIND>

export type LightingEditableField = ValueOf<typeof LIGHTING_EDITABLE_FIELD>

/** 统一灯效定义，manifest 中所有灯光元数据只使用这一种结构。 */
export interface LightingEffectDefinition {
  /** 固件灯效 ID。 */
  id: number
  /** 国际化 key，引用 lighting.effect 下的 key。 */
  i18nKey: string
  /** 灯效作用目标。 */
  target: LightingTarget
  /** 灯效类型，用于 UI 判断图标和 SDK 判断读写领域。 */
  kind: LightingEffectKind
  /** 当前灯效支持的可编辑字段。 */
  editableFields: LightingEditableField[]
  /**
   * 速度调节范围（可选，默认 0-100 连续调节）。
   * GCOME_AP 等按档位调整的设备配置为 { min: 0, max: 4, step: 1 }，
   * UI 端 speedSlider 会按 max/step 渲染为档位滑块。
   */
  speedRange?: { min: number; max: number; step: number }
  /**
   * 亮度调节范围（可选，默认 0-100 步进 1 连续调节）。
   * 协议亮度本身为 0-100；GCOME_AP 为与速度统一 5 档配置 { min: 0, max: 100, step: 25 }，
   * UI 端 brightSlider 按 step 量化为 0/25/50/75/100 档位值（显示为纯数值），其余设备按百分比显示。
   */
  brightnessRange?: { min: number; max: number; step: number }
  /** 键盘静态灯效类型，来自固件静态模式定义。 */
  staticType?: number
  /** 自定义灯效标识。 */
  isCustom?: boolean
  /**
   * 软件灯效模式标识（音乐律动等非固件灯效）。
   * 与 id 并存：id 供灯效列表选中，modeKey 对应算法实现（如 AudioRhythmMode）。
   */
  modeKey?: string
  /** 可选提示文案 key，例如电量优先灯效提示。 */
  tip?: string
}

/** 设备清单中的统一灯光能力配置。 */
export interface LightingCapability {
  type: 'lighting'
  config: {
    enable: boolean
    backlightSwitch?: boolean
    /** 是否允许用户新增/删除自定义灯效；未填视为允许。 */
    canAdd?: boolean
    /**
     * 灯区 Tab 文案覆盖。协议层仍按 target 区分（如 Side），仅影响 UI 展示名。
     * 未填时使用默认 lighting.tabs.*。
     */
    tabI18nKeys?: Partial<Record<KeyboardLightingTarget, string>>
    effects: LightingEffectDefinition[]
  }
}

export function hasLightingEditableField(
  effect: Pick<LightingEffectDefinition, 'editableFields'> | undefined | null,
  field: LightingEditableField,
): boolean {
  return Boolean(effect?.editableFields?.includes(field))
}
